<template>
  <div class="reviewer-group setting-section">
    <div class="setting-title m-b-22 flex row-between">
      <span>组建评审小组</span>
      <el-button v-if="beforeStart" type="primary" @click="openReview">
        开启评审
      </el-button>
    </div>
    <el-form ref="listForm" :model="form">
      <el-table :data="form.list" border>
        <el-table-column label="序号" type="index" width="90px">
          <template v-if="!readOnly" v-slot:default="{ $index }">
            <el-form-item class="m-t-18">
              <span>{{ $index + 1 }}</span>
            </el-form-item>
          </template>
        </el-table-column>
        <el-table-column label="评审人员" prop="expertName">
          <template v-if="!readOnly" v-slot:header>
            <span class="require-tag">*</span>
            <span>评审人员</span>
          </template>
          <template v-slot:default="{ row, $index }">
            <span v-if="readOnly">{{ row.expertName }}</span>
            <el-form-item
              v-else
              :prop="'list.' + $index + '.expertName'"
              :rules="rules.expertName"
              class="m-t-18"
            >
              <el-input
                v-model.trim="row.expertName"
                placehlder="请输入"
                :maxlength="50"
                show-word-limit
              />
            </el-form-item>
          </template>
        </el-table-column>
        <el-table-column label="手机号码" prop="expertMobile">
          <template v-if="!readOnly" v-slot:header>
            <span class="require-tag">*</span>
            <span>手机号码</span>
          </template>
          <template v-slot:default="{ row, $index }">
            <span v-if="row.readOnly">{{ row.expertMobile }}</span>
            <el-form-item
              v-else
              :prop="'list.' + $index + '.expertMobile'"
              :rules="rules.expertMobile"
              class="m-t-18"
            >
              <el-input
                v-model.trim="row.expertMobile"
                placehlder="请输入"
                :maxlength="50"
                show-word-limit
              />
            </el-form-item>
          </template>
        </el-table-column>
        <el-table-column
          v-if="showOperator && (beforeStart || reviewEnd)"
          label="操作"
          width="120px"
        >
          <template v-slot:default="{ row, $index }">
            <template v-if="reviewEnd">
              <el-button
                v-if="row.readOnly"
                type="text"
                @click="onChangeExpert(row, $index)"
              >
                修改手机号码
              </el-button>
              <el-button v-else type="text" @click="confirmChangeExpert(row)">
                确认修改
              </el-button>
            </template>
            <el-form-item v-if="beforeStart" class="m-t-18">
              <el-button
                type="text"
                class="danger-button"
                @click="removeItem($index)"
              >
                删除
              </el-button>
            </el-form-item>
          </template>
        </el-table-column>
      </el-table>
    </el-form>
    <div v-if="beforeStart" class="add-button" @click="addItem">
      <i class="el-icon-plus"></i>
      新增人员
    </div>
  </div>
</template>

<script>
import { mobileValidate, requiredRule } from '@/utils/form-validate'
import { onlineReviewStatusDict } from '@/dictionary/project/online-review'
import {
  changePurchaseReviewerApi,
  setUpPurchaseReviewerListApi
} from '@/api/procure/purchase-review'
import { cloneDeep, isEqual, pick } from 'lodash'

/**
 * 评委小组成员登记，项目负责人登记评审时使用
 */
export default {
  name: 'OnlineReviewerRegistration',

  inject: [
    'provideReviewId',
    'provideOrderId',
    'providePurchaseType',
    'provideBeforeStart',
    'provideReviewClosed'
  ],

  props: {
    list: {
      type: Array,
      default: () => []
    },

    // 是否显示操作，在查询统计中不显示
    showOperator: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      form: {
        list: []
      },
      rules: {
        expertName: requiredRule('评审人员姓名不能为空'),
        expertMobile: [
          requiredRule('手机号码不能为空'),
          {
            pattern: mobileValidate().pattern,
            message: '请输入正确的11位手机号码',
            trigger: 'blur'
          }
        ]
      }
    }
  },

  computed: {
    reviewId() {
      return this.provideReviewId()
    },

    orderId() {
      return this.provideOrderId()
    },

    purchaseType() {
      return this.providePurchaseType()
    },

    beforeStart() {
      return this.provideBeforeStart()
    },

    reviewEnd() {
      return this.provideReviewClosed()
    },

    readOnly() {
      return !this.beforeStart
    }
  },

  watch: {
    list: {
      handler(val) {
        if (val?.length) {
          this.form.list = cloneDeep(val).map(item => {
            item.readOnly = !this.beforeStart
            return item
          })
        }
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    addItem() {
      // 最多添加9个专家
      const maxNum = 9
      if (this.form.list.length >= maxNum) {
        this.$message.warn('最多添加9个评委')
        return
      }
      const reviewer = {
        // 评审人员姓名
        expertName: '',
        // 手机号码
        expertMobile: ''
      }
      this.form.list.push(reviewer)
    },

    removeItem(index) {
      this.form.list.splice(index, 1)
    },

    onChangeExpert(row, index) {
      row.readOnly = false
    },

    confirmChangeExpert(row) {
      changePurchaseReviewerApi(this.reviewId, row.reviewerId, row)
        .then(res => {
          this.$message.success('修改成功')
          this.$emit('afterChange')
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    openReview() {
      if (!this.form.list?.length) {
        this.$message.warning('请至少添加一名评审人员')
        return
      }

      setUpPurchaseReviewerListApi(this.reviewId, this.form.list)
        .then(() => {
          this.$emit('openReview')
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    validateTable() {
      return new Promise((resolve, reject) => {
        this.$refs.listForm.validate(valid => {
          if (!valid) {
            reject({ message: '请完善评委信息' })
            return
          }

          resolve()
        })
      })
    }
  }
}
</script>

<style scoped lang="scss">
.setting-section {
  .setting-title {
    font-size: 16px;
    color: $text-color;
  }

  .require-tag {
    color: $danger-color;
    margin-right: 8px;
  }

  .add-button {
    width: 100%;
    padding: 4px 0;
    border: 1px dashed $border-color;
    border-radius: 4px;
    text-align: center;
    margin-top: 16px;
    cursor: pointer;

    &:hover {
      border-color: $primary-color;
      color: $primary-color;
    }
  }
}
</style>
